import type { OreDef, ToolDef } from './types';

/**
 * Mine layers. Each deeper tier is worth ~×5–8 per unit but costs ~×10–15 to
 * unlock and is harder to mine — creating the "deepen vs widen" decision.
 */
export const ORES: readonly OreDef[] = [
  { id: 'stone',   name: 'Stein',   depth: 0, baseValue: 1,        hardness: 1,    requiredToolLevel: 1,  unlockCost: 0,        color: '#9aa7b8', icon: '🪨' },
  { id: 'coal',    name: 'Kohle',   depth: 1, baseValue: 6,        hardness: 1.6,  requiredToolLevel: 1,  unlockCost: 400,      color: '#3b3b44', icon: '⚫' },
  { id: 'copper',  name: 'Kupfer',  depth: 2, baseValue: 30,       hardness: 2.4,  requiredToolLevel: 2,  unlockCost: 7_000,    color: '#c87f4a', icon: '🟤' },
  { id: 'iron',    name: 'Eisen',   depth: 3, baseValue: 150,      hardness: 3.6,  requiredToolLevel: 3,  unlockCost: 1.2e5,    color: '#b8bcc4', icon: '⚙️' },
  { id: 'silver',  name: 'Silber',  depth: 4, baseValue: 750,      hardness: 5.5,  requiredToolLevel: 4,  unlockCost: 2.5e6,    color: '#dfe7f0', icon: '🪙' },
  { id: 'gold',    name: 'Gold',    depth: 5, baseValue: 3_600,    hardness: 8.5,  requiredToolLevel: 5,  unlockCost: 6e7,      color: '#ffd25a', icon: '🟡' },
  { id: 'emerald', name: 'Smaragd', depth: 6, baseValue: 17_000,   hardness: 13,   requiredToolLevel: 6,  unlockCost: 1.2e9,    color: '#3ddc84', icon: '💚' },
  { id: 'diamond', name: 'Diamant', depth: 7, baseValue: 80_000,   hardness: 20,   requiredToolLevel: 7,  unlockCost: 3e10,     color: '#5fd0ff', icon: '💎' },
  { id: 'mithril', name: 'Mithril', depth: 8, baseValue: 3.8e5,    hardness: 30,   requiredToolLevel: 8,  unlockCost: 9e11,     color: '#7af0ff', icon: '🔷' },
  { id: 'uranium', name: 'Uran',    depth: 9, baseValue: 1.8e6,    hardness: 46,   requiredToolLevel: 9,  unlockCost: 3e13,     color: '#9dff5f', icon: '☢️' },
] as const;

export const ORE_BY_ID: Record<string, OreDef> = Object.fromEntries(ORES.map((o) => [o.id, o]));
export const ORE_BY_DEPTH: Record<number, OreDef> = Object.fromEntries(ORES.map((o) => [o.depth, o]));
export const MAX_DEPTH = ORES.length - 1;

/** Pickaxe / drill tool tiers. Upgrading boosts click power & lets you mine deeper layers efficiently. */
export const TOOLS: readonly ToolDef[] = [
  { level: 1,  name: 'Holzspitzhacke',  icon: '⛏️', clickPower: 1,      cost: 0 },
  { level: 2,  name: 'Steinspitzhacke', icon: '⛏️', clickPower: 3,      cost: 250 },
  { level: 3,  name: 'Eisenspitzhacke', icon: '⛏️', clickPower: 9,      cost: 3_000 },
  { level: 4,  name: 'Stahlbohrer',     icon: '🔩', clickPower: 30,     cost: 40_000 },
  { level: 5,  name: 'Diamantbohrer',   icon: '🔩', clickPower: 110,    cost: 5e5 },
  { level: 6,  name: 'Plasmaschneider', icon: '🔦', clickPower: 420,    cost: 1.8e7 },
  { level: 7,  name: 'Laserbohrer',     icon: '🔆', clickPower: 1_700,  cost: 3.5e8 },
  { level: 8,  name: 'Quantenbohrer',   icon: '🌀', clickPower: 7_000,  cost: 9e9 },
  { level: 9,  name: 'Antimaterie-Lanze', icon: '✦', clickPower: 30_000, cost: 2.4e11 },
  { level: 10, name: 'Singularitäts-Rig', icon: '🛸', clickPower: 130_000, cost: 7e12 },
] as const;

export const TOOL_BY_LEVEL: Record<number, ToolDef> = Object.fromEntries(TOOLS.map((t) => [t.level, t]));
export const MAX_TOOL_LEVEL = TOOLS.length;
